import React from "react";
import "./App.scss";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import MainLayout from "./layouts/MainLayout";
import { PageLoadingSpinner } from "./share-components/PageLoadingSpinner";
import {
  PAGE_ADMIN_ACCOUNTS, PAGE_ADMIN_DASHBOARD, PAGE_CATEGORY_LIST, PAGE_ACCOUNT_CREATE,
  PAGE_CATEGORY_CREATE, PAGE_LOGIN, PAGE_NOT_ALLOWED, PAGE_SERVER_ERROR, PREFIX_ROUTE_ACCOUNT,
  PREFIX_ROUTE_ADMIN, PREFIX_ROUTE_AUTH, ROUTE_LOGIN, PAGE_PRODUCT_LIST, PAGE_PRODUCT_CREATE, PAGE_PRODUCT_EDIT,
  PAGE_PRODUCT_DETAILS, PAGE_PURCHASE_LIST, PAGE_PURCHASE_EDIT, PAGE_PURCHASE_DETAILS,
  PAGE_PURCHASE_PRODUCT,
  PREFIX_ROUTE_CATEGORY,
  PREFIX_ROUTE_PRODUCT,
  PREFIX_ROUTE_PURCHASE,
  PREFIX_ROUTE_ORDER,
  PAGE_ORDER_CREATE,
  PAGE_ORDER_DETAILS,
  PAGE_ORDER_EDIT,
  PAGE_ORDER_LIST,
  PAGE_NOT_FOUND,
  PAGE_PROFIT_LOSS_CALCULATE,
  PAGE_MONTHLY_REPORT,
  PAGE_POSTED_LINKS,
  PAGE_SHOP_LIST,
  PAGE_ADD_SHOP,
  PAGE_OUTOFSTOCKS,
  ROUTE_OUTOFSTOCKS,
  PAGE_MONTHLY_REPORTS,
  ROUTE_ADMIN_ACCOUNTS,
  ROUTE_ADMIN_DASHBOARD,
  ROUTE_CATEGORY_LIST,
  ROUTE_PRODUCT_LIST,
  ROUTE_PURCHASE_LIST,
  ROUTE_ORDER_LIST,
  PREFIX_ROUTE_GENERAL_EXPEND,
  ROUTE_GENERAL_EXPEND_LIST,
  PAGE_ADD_GENERAL_EXPEND,
  PAGE_GENERAL_EXPEND_DETAILS,
  PAGE_EDIT_GENERAL_EXPEND,
  PAGE_GENERAL_EXPEND_LIST,
  PAGE_ORDER_DELI_EDIT,
  PREFIX_ROUTE_SHOP,
  ROUTE_SHOP_LIST,
  PREFIX_ROUTE_MONEY_ADJUSTMENT,
  ROUTE_MONEY_ADJUSTMENT_LIST,
  PAGE_ADD_MONEY_ADJUSTMENT,
  PAGE_EDIT_MONEY_ADJUSTMENT,
  PAGE_MONEY_ADJUSTMENT_DETAILS,
  PAGE_MONEY_ADJUSTMENT_LIST,
  PREFIX_ROUTE_PRINT_PDF,
  ROUTE_PRINT_PDF,
  PAGE_PRINT_PDF,
  ROUTE_INSTOCKS,
  PREFIX_ROUTE_INSTOCK_ADJUSTMENT,
  ROUTE_SEND_STOCK_HISTORIES
} from "./constants/routeConstants";
import NotAllowed from "./pages/auth/NotAllowed";
import SystemError from "./pages/auth/SystemError";
import Login from "./pages/auth/Login";
import PageNotFound from "./pages/auth/PageNotFound";
import { AppProvider, useAppContext } from "./share-components/AppContext";
import AlertDialog from "./share-components/AlertDialog";
import AdminList from "./pages/account/AdminList";
import { isNullOrEmptyString } from "./services/UtilityService";
import Dashboard from "./pages/admin/Dashboard";
import AuthRoleBaseRoute from "./share-components/AuthRoleBaseRoute";
import { RG_ADMIN, RG_SYSTEM_ADMIN } from "./constants/roleConstants";
import CreateAccount from "./pages/account/CreateAccount";
import CategoryCreate from "./pages/admin/CategoryCreate";
import CategoryList from "./pages/admin/CategoryList";
import ProductList from "./pages/admin/ProductList";
import ProductCreate from "./pages/admin/ProductCreate";
import ProductEdit from "./pages/admin/ProductEdit";
import ProductDetails from "./pages/admin/ProductDetails";
import PurchaseList from "./pages/admin/PurchaseList";
import PurchaseProduct from "./pages/admin/PurchaseProduct";
import PurchaseEdit from "./pages/admin/PurchaseEdit";
import PurchaseDetails from "./pages/admin/PurchaseDetails";
import OrderDetails from "./pages/admin/OrderDetails";
import OrderCreate from "./pages/admin/OrderCreate";
import OrderEdit from "./pages/admin/OrderEdit";
import OrderList from "./pages/admin/OrderList";
import ErrorAlertMessageBody from "./share-components/ErrorAlertMessageBody";
import { MAIN_MENU_GENERAL_EXPEND, MAIN_MENU_MONEY_ADJUSTMENT, MAIN_MENU_ORDER, MAIN_MENU_PRODUCT, MAIN_MENU_PURCHASE } from "./constants/commonConstants";
import ProfitOrLossCalculate from "./pages/admin/ProfitOrLossCalculate";
import PostedLink from "./pages/admin/PostedLink";
import ShopList from "./pages/admin/ShopList";
import AddShop from "./pages/admin/AddShop";
import ApiSessionHandler from "./share-components/ApiSessionHandler";
import LockedMonthlyReport from "./pages/admin/LockedMonthlyReport";
import AddGeneralExpend from "./pages/generalExpend/AddGeneralExpend";
import GeneralExpendDetails from "./pages/generalExpend/GeneralExpendDetails";
import EditGeneralExpend from "./pages/generalExpend/EditGeneralExpend";
import GeneralExpendList from "./pages/generalExpend/GeneralExpendList";
import OrderDeliEdit from "./pages/admin/OrderDeliEdit";
import AddMoneyAdjustment from "./pages/moneyAdjustment/AddMoneyAdjustment";
import EditMoneyAdjustment from "./pages/moneyAdjustment/EditMoneyAdjustment";
import MoneyAdjustmentDetails from "./pages/moneyAdjustment/MoneyAdjustmentDetails";
import MoneyAdjustmentList from "./pages/moneyAdjustment/MoneyAdjustmentList";
import PrintPDF from "./pages/printPDF/PrintPDF";
import InstockAdjustmentList from "./pages/instockAdjustment/InstockAdjustmentList";
import SendItemHistories from "./pages/instockAdjustment/SendItemHistories";
import OutOfStocks from "./pages/instockAdjustment/OutOfStocks";

const TimeOutSessionHandler = () => {
  const { tokenTimeout, setTokenTimeout } = useAppContext();

  return (
    <AlertDialog
      dialogId="sessionTimeoutDialog"
      showDialog={tokenTimeout}
      body="Session has expired. Please login again!"
      onConfirm={() => setTokenTimeout(false)}
    />
  );
};

const ServerErrorDialog = () => {
  const { showApiErrorMessage, setShowApiErrorMessage } = useAppContext();

  return (
    <AlertDialog
      dialogId="serverErrorDialog"
      showDialog={!isNullOrEmptyString(showApiErrorMessage)}
      body={<ErrorAlertMessageBody errorMessage={showApiErrorMessage} />}
      onConfirm={() => setShowApiErrorMessage(null)}
    />
  );
};

function App() {
  return (
    <Router>
      <AppProvider>
        <ApiSessionHandler />
        <PageLoadingSpinner />
        <TimeOutSessionHandler />
        <ServerErrorDialog />
        <Routes>
          //#region Auth Section
          <Route path="/" element={<Navigate to={ROUTE_LOGIN} replace />} />
          <Route path={PAGE_LOGIN} element={<Login />} />
          <Route path={PREFIX_ROUTE_AUTH}>
            <Route path="" element={<Navigate to={ROUTE_LOGIN} />} />
            <Route path={PAGE_LOGIN} element={<Navigate to={ROUTE_LOGIN} />} />
            <Route element={<MainLayout />}>
              <Route path={PAGE_NOT_ALLOWED} element={<NotAllowed />} />
              <Route path={PAGE_SERVER_ERROR} element={<SystemError />} />
            </Route>
          </Route>
          //#endregion

          //#region Account
          <Route path={PREFIX_ROUTE_ACCOUNT} element={<MainLayout />}>
            <Route path="" element={<Navigate to={ROUTE_ADMIN_ACCOUNTS} />} />
            <Route path={PAGE_ADMIN_ACCOUNTS}
              element={
                <AuthRoleBaseRoute
                  element={<AdminList />}
                  allowedRoles={RG_SYSTEM_ADMIN}
                />
              } />
            <Route path={PAGE_ACCOUNT_CREATE}
              element={
                <AuthRoleBaseRoute
                  element={<CreateAccount />}
                  allowedRoles={RG_SYSTEM_ADMIN}
                />
              } />
          </Route>
          //#endregion

          //#region Management
          <Route path={PREFIX_ROUTE_ADMIN} element={<MainLayout />}>
            {/* <Route path="/" element={<Navigate to={ROUTE_OUTOFSTOCKS} replace />} /> */}
            <Route path="" element={<Navigate to={ROUTE_ADMIN_DASHBOARD} />} />
            <Route path={PAGE_ADMIN_DASHBOARD} element={
              <AuthRoleBaseRoute
                element={<Dashboard />}
                allowedRoles={RG_ADMIN}
              />
            } />

            <Route path={PAGE_PROFIT_LOSS_CALCULATE} element={
              <AuthRoleBaseRoute
                element={<ProfitOrLossCalculate />}
                allowedRoles={RG_ADMIN}
              // mainMenu={MAIN_MENU_MANAGEMENT}
              />
            } />

            <Route path={PAGE_MONTHLY_REPORTS} element={
              <AuthRoleBaseRoute
                element={<LockedMonthlyReport />}
                allowedRoles={RG_ADMIN}
              // mainMenu={MAIN_MENU_MANAGEMENT}
              />
            } />

            <Route path={PAGE_POSTED_LINKS} element={
              <AuthRoleBaseRoute
                element={<PostedLink />}
                allowedRoles={RG_ADMIN}
              // mainMenu={MAIN_MENU_MANAGEMENT}
              />
            } />
            
              <Route path={PAGE_PRINT_PDF} element={
              <AuthRoleBaseRoute
                element={<PrintPDF />}
                allowedRoles={RG_ADMIN}
              />
            } />
          </Route>
          //#endregion

          //#region Shop
          <Route path={PREFIX_ROUTE_SHOP} element={<MainLayout />}>
            {/* <Route path="/" element={<Navigate to={ROUTE_OUTOFSTOCKS} replace />} /> */}
            <Route path="" element={<Navigate to={ROUTE_SHOP_LIST} />} />

            <Route path={PAGE_SHOP_LIST}
              element={
                <AuthRoleBaseRoute
                  element={<ShopList />}
                  allowedRoles={RG_ADMIN}
                />
              } />
            <Route path={PAGE_ADD_SHOP}
              element={
                <AuthRoleBaseRoute
                  element={<AddShop />}
                  allowedRoles={RG_ADMIN}
                />
              } />
          </Route>
          //#endregion

          //#region Category
          <Route path={PREFIX_ROUTE_CATEGORY} element={<MainLayout />}>
            <Route path="" element={<Navigate to={ROUTE_CATEGORY_LIST} />} />
            <Route path={PAGE_CATEGORY_CREATE} element={
              <AuthRoleBaseRoute
                element={<CategoryCreate />}
                allowedRoles={RG_ADMIN}
              />
            } />
            <Route path={PAGE_CATEGORY_LIST} element={
              <AuthRoleBaseRoute
                element={<CategoryList />}
                allowedRoles={RG_ADMIN}
              />
            } />
          </Route>
          //#endregion

          //#region Product
          <Route path={PREFIX_ROUTE_PRODUCT} element={<MainLayout />}>
            <Route path="" element={<Navigate to={ROUTE_PRODUCT_LIST} />} />
            <Route path={PAGE_PRODUCT_LIST} element={
              <AuthRoleBaseRoute
                element={<ProductList />}
                allowedRoles={RG_ADMIN}
              />
            } />
            <Route path={PAGE_PRODUCT_CREATE} element={
              <AuthRoleBaseRoute
                element={<ProductCreate />}
                allowedRoles={RG_ADMIN}
              />
            } />
            <Route path={`${PAGE_PRODUCT_EDIT}/:productId`} element={
              <AuthRoleBaseRoute
                element={<ProductEdit />}
                allowedRoles={RG_ADMIN}
                mainMenu={MAIN_MENU_PRODUCT}
              />
            } />
            <Route path={`${PAGE_PRODUCT_DETAILS}/:productId`} element={
              <AuthRoleBaseRoute
                element={<ProductDetails />}
                allowedRoles={RG_ADMIN}
                mainMenu={MAIN_MENU_PRODUCT}
              />
            } />
          </Route>
          //#endregion

          //#region Purchase
          <Route path={PREFIX_ROUTE_PURCHASE} element={<MainLayout />}>
            <Route path="" element={<Navigate to={ROUTE_PURCHASE_LIST} />} />
            <Route path={PAGE_PURCHASE_LIST} element={
              <AuthRoleBaseRoute
                element={<PurchaseList />}
                allowedRoles={RG_ADMIN}
              />
            } />
            <Route path={`${PAGE_PURCHASE_PRODUCT}`} element={
              <AuthRoleBaseRoute
                element={<PurchaseProduct />}
                allowedRoles={RG_ADMIN}
              />
            } />
            <Route path={`${PAGE_PURCHASE_EDIT}/:purchaseId`} element={
              <AuthRoleBaseRoute
                element={<PurchaseEdit />}
                allowedRoles={RG_ADMIN}
                mainMenu={MAIN_MENU_PURCHASE}
              />
            } />
            <Route path={`${PAGE_PURCHASE_DETAILS}/:purchaseId`} element={
              <AuthRoleBaseRoute
                element={<PurchaseDetails />}
                allowedRoles={RG_ADMIN}
                mainMenu={MAIN_MENU_PURCHASE}
              />
            } />
          </Route>
          #endregion

          //#region Order
          <Route path={PREFIX_ROUTE_ORDER} element={<MainLayout />}>
            <Route path="" element={<Navigate to={ROUTE_ORDER_LIST} />} />
            <Route path={PAGE_ORDER_CREATE} element={
              <AuthRoleBaseRoute
                element={<OrderCreate />}
                allowedRoles={RG_ADMIN}
              />
            } />
            <Route path={`${PAGE_ORDER_DETAILS}/:orderId`} element={
              <AuthRoleBaseRoute
                element={<OrderDetails />}
                allowedRoles={RG_ADMIN}
                mainMenu={MAIN_MENU_ORDER}
              />
            } />
            <Route path={`${PAGE_ORDER_DELI_EDIT}/:orderId`} element={
              <AuthRoleBaseRoute
                element={<OrderDeliEdit />}
                allowedRoles={RG_ADMIN}
                mainMenu={MAIN_MENU_ORDER}
              />
            } />
            <Route path={`${PAGE_ORDER_EDIT}/:orderId`} element={
              <AuthRoleBaseRoute
                element={<OrderEdit />}
                allowedRoles={RG_ADMIN}
                mainMenu={MAIN_MENU_ORDER}
              />
            } />
            <Route path={PAGE_ORDER_LIST} element={
              <AuthRoleBaseRoute
                element={<OrderList />}
                allowedRoles={RG_ADMIN}
              />
            } />
          </Route>
          #endregion

          //#region General Expend
          <Route path={PREFIX_ROUTE_GENERAL_EXPEND} element={<MainLayout />}>
            <Route path="" element={<Navigate to={ROUTE_GENERAL_EXPEND_LIST} />} />
            <Route path={PAGE_ADD_GENERAL_EXPEND} element={
              <AuthRoleBaseRoute
                element={<AddGeneralExpend />}
                allowedRoles={RG_ADMIN}
              />
            } />
            <Route path={`${PAGE_EDIT_GENERAL_EXPEND}/:generalExpendId`} element={
              <AuthRoleBaseRoute
                element={<EditGeneralExpend />}
                allowedRoles={RG_ADMIN}
                mainMenu={MAIN_MENU_GENERAL_EXPEND}
              />
            } />
            <Route path={`${PAGE_GENERAL_EXPEND_DETAILS}/:generalExpendId`} element={
              <AuthRoleBaseRoute
                element={<GeneralExpendDetails />}
                allowedRoles={RG_ADMIN}
                mainMenu={MAIN_MENU_GENERAL_EXPEND}
              />
            } />
            <Route path={PAGE_GENERAL_EXPEND_LIST} element={
              <AuthRoleBaseRoute
                element={<GeneralExpendList />}
                allowedRoles={RG_ADMIN}
              />
            } />
          </Route>
          //#endregion

          //#region Money Adjustment
          <Route path={PREFIX_ROUTE_MONEY_ADJUSTMENT} element={<MainLayout />}>
            <Route path="" element={<Navigate to={ROUTE_MONEY_ADJUSTMENT_LIST} />} />
            <Route path={PAGE_ADD_MONEY_ADJUSTMENT} element={
              <AuthRoleBaseRoute
                element={<AddMoneyAdjustment />}
                allowedRoles={RG_ADMIN}
              />
            } />
            <Route path={`${PAGE_EDIT_MONEY_ADJUSTMENT}/:moneyAdjustmentId`} element={
              <AuthRoleBaseRoute
                element={<EditMoneyAdjustment />}
                allowedRoles={RG_ADMIN}
                mainMenu={MAIN_MENU_MONEY_ADJUSTMENT}
              />
            } />
            <Route path={`${PAGE_MONEY_ADJUSTMENT_DETAILS}/:moneyAdjustmentId`} element={
              <AuthRoleBaseRoute
                element={<MoneyAdjustmentDetails />}
                allowedRoles={RG_ADMIN}
                mainMenu={MAIN_MENU_MONEY_ADJUSTMENT}
              />
            } />
            <Route path={PAGE_MONEY_ADJUSTMENT_LIST} element={
              <AuthRoleBaseRoute
                element={<MoneyAdjustmentList />}
                allowedRoles={RG_ADMIN}
              />
            } />
          </Route>
          //#endregion

          //#region Instock Adjustment
          <Route path={PREFIX_ROUTE_INSTOCK_ADJUSTMENT} element={<MainLayout />}>
            {/* <Route path="/" element={<Navigate to={ROUTE_OUTOFSTOCKS} replace />} /> */}
            <Route path="" element={<Navigate to={ROUTE_INSTOCKS} />} />

            <Route path={ROUTE_OUTOFSTOCKS} element={
              <AuthRoleBaseRoute
                element={<OutOfStocks />}
                allowedRoles={RG_ADMIN}
              />
            } />

            <Route path={ROUTE_INSTOCKS} element={
              <AuthRoleBaseRoute
                element={<InstockAdjustmentList />}
                allowedRoles={RG_ADMIN}
              />
            } />
            
            <Route path={ROUTE_SEND_STOCK_HISTORIES} element={
              <AuthRoleBaseRoute
                element={<SendItemHistories />}
                allowedRoles={RG_ADMIN}
              />
            } />

          </Route>
          //#endregion

          {/* <Route path={PageNotFound} element={<PageNotFound />} /> */}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </AppProvider>
    </Router>
  );
}
export default App;
